// 두 환경음 전용 채널. 프레임마다 노드·객체를 만들지 않고 시작/중지 경계에서만 소스를 만든다.
import { VILLAGE } from '../shared/levels';
import { Scene, type GameView } from '../shared/types';
import { dbToGain } from './duck';
import { panFor } from './decide';
import { S, SFX_BY_ID } from './sounds';

const gate = VILLAGE.exits.find((e) => e.to === 'r2')!;
export const EMBER_GATE = { x: (gate.rect.x0 + gate.rect.x1) / 2, z: (gate.rect.z0 + gate.rect.z1) / 2 };
export function gateAttenuation(x: number, z: number): number {
  return Math.max(0, Math.min(1, (14 - Math.hypot(x - EMBER_GATE.x, z - EMBER_GATE.z)) / 12));
}

class LoopChannel {
  private source: AudioBufferSourceNode | null = null;
  readonly gain: GainNode;
  private readonly pan: StereoPannerNode;
  starts = 0;
  stops = 0;
  constructor(private readonly ctx: AudioContext, bus: AudioNode, readonly id: number) {
    this.gain = ctx.createGain(); this.pan = ctx.createStereoPanner();
    this.gain.connect(this.pan); this.pan.connect(bus);
  }
  get active(): boolean { return this.source !== null; }
  update(buffer: AudioBuffer | null, amount: number, pan: number): void {
    if (!buffer || amount <= 0) { this.stop(); return; }
    const t = this.ctx.currentTime, gain = dbToGain(SFX_BY_ID[this.id].gainDb) * amount;
    this.pan.pan.value = pan;
    if (!this.source) {
      this.source = this.ctx.createBufferSource(); this.source.buffer = buffer; this.source.loop = true;
      this.source.connect(this.gain);
      this.gain.gain.cancelScheduledValues(t); this.gain.gain.setValueAtTime(0, t);
      this.gain.gain.setTargetAtTime(gain, t, 0.04); this.source.start(t); this.starts++;
    } else this.gain.gain.setTargetAtTime(gain, t, 0.04);
  }
  stop(): void {
    if (!this.source) return;
    this.source.stop(); this.source.disconnect(); this.source = null; this.stops++;
    this.gain.gain.cancelScheduledValues(this.ctx.currentTime); this.gain.gain.value = 0;
  }
}

export class AmbiencePlayer {
  private readonly canyon: LoopChannel;
  private readonly gate: LoopChannel;
  private serial = -1;
  constructor(ctx: AudioContext, bus: AudioNode, private readonly buffers: readonly (AudioBuffer | null)[]) {
    this.canyon = new LoopChannel(ctx, bus, S.r2Ambience);
    this.gate = new LoopChannel(ctx, bus, S.villageGateEmber);
  }
  update(view: GameView, enabled: boolean): void {
    if (view.flow.sceneSerial !== this.serial) { this.stop(); this.serial = view.flow.sceneSerial; }
    const ready = enabled && !view.flow.paused;
    const canyon = ready && view.flow.scene === Scene.Dungeon && view.flow.mapId === 'r2';
    const village = ready && view.flow.scene === Scene.Village;
    this.canyon.update(this.buffers[S.r2Ambience], canyon ? 1 : 0, 0);
    this.gate.update(this.buffers[S.villageGateEmber], village ? gateAttenuation(view.hero.x, view.hero.z) : 0, panFor(EMBER_GATE.x - view.hero.x));
  }
  stop(): void { this.canyon.stop(); this.gate.stop(); }
  stats(): unknown {
    return { r2Ambience: { active: this.canyon.active, starts: this.canyon.starts, stops: this.canyon.stops }, villageGateEmber: { active: this.gate.active, starts: this.gate.starts, stops: this.gate.stops } };
  }
}
